import React, { useEffect, useState, useRef } from "react";
import { searchProducts, addProduct, updateProduct, deleteProduct } from "../services/productService";
import { getCategories } from "../services/categoryService";
import { priceInfo } from '../utils/priceUtils';
import { useCurrency } from '../context/CurrencyContext';
import { hasPermission } from "../services/authService";
import { useNavigate } from "react-router-dom";
import './Admin.css';
import toast from 'react-hot-toast';

export default function AdminProducts() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterCategory, setFilterCategory] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const searchDebounce = useRef(null);
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const { formatPrice } = useCurrency();

  // Form State
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(getInitialForm());
  const [availableServices, setAvailableServices] = useState([]);
  const [showServicesModal, setShowServicesModal] = useState(false);
  const [draggedServiceId, setDraggedServiceId] = useState(null);

  function getInitialForm() {
    return {
      name: "", description: "", price: "", salePrice: "", offerPrice: '', offerFrom: '', offerTo: '',
      category: "", categoryId: "", sku: "", brand: "", garancia: "", modeli: "", stock: "", specs: "", specValues: {},
      images: [], mainImageIndex: 0, serviceIds: [], isActive: true
    };
  }

  useEffect(() => {
    if (!hasPermission('admin.products')) {
      navigate('/login');
      return;
    }

    let cancelled = false;

    const init = async () => {
      try {
        const cats = await getCategories();
        if (cancelled) return;
        setCategories(cats || []);

        // Initial load: include inactive; optional q/category from current state
        const params = { includeInactive: 'true' };
        if (filterCategory) params.category = filterCategory;
        if (searchQ) params.q = searchQ;
        const prods = await searchProducts(params);
        if (cancelled) return;
        setProducts(prods || []);
        setPage(1);

        // Load services lazily or upfront? Upfront is easier for the form
        try {
          const svcModule = await import('../services/serviceService');
          const arr = await svcModule.listServices();
          if (cancelled) return;
          setAvailableServices(arr || []);
        } catch (e) {
          console.error(e);
        }
      } catch (e) {
        console.error(e);
      }
    };

    init();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const loadProducts = async (opts = {}) => {
    try {
      const params = { includeInactive: 'true' };
      const category = opts.category ?? filterCategory;
      const q = opts.q ?? searchQ;
      if (category) {
        if (!isNaN(category)) params.categoryId = category;
        else params.category = category;
      }
      if (q) params.q = q;
      const prods = await searchProducts(params);
      setProducts(prods || []);
      setPage(1);
    } catch (e) {
      console.error('Failed to load products', e);
      setProducts([]);
    }
  };

  const handleFilterChange = (cat, q) => {
    setFilterCategory(cat);
    setSearchQ(q);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      loadProducts({ category: cat, q: q });
    }, 350);
  };

  const handleStartAdd = () => {
    setEditId(null);
    setForm(getInitialForm());
    setShowForm(true);
  };

  const handleStartEdit = (p) => {
    setEditId(p.id);
    // Logic to find category ID
    // Logic to find category ID
    // let categoryValue = "";
    const findCat = (nodes, name) => {
      for (const n of nodes) {
        if (!n) continue;
        if (n.name === name) return n;
        if (Array.isArray(n.subcategories)) { const r = findCat(n.subcategories, name); if (r) return r; }
      }
      return null;
    };
    if (p.category) {
      const matched = findCat(categories, p.category);
      if (matched) { /* categoryValue = matched.id; */ }
    }

    // Spec values
    let initialSpecValues = {};
    if (p.specs) {
      try {
        const parsed = typeof p.specs === 'string' ? JSON.parse(p.specs) : p.specs;
        if (parsed && typeof parsed === 'object') initialSpecValues = parsed;
      } catch (_) { }
    }

    // Images
    let mainIndex = 0;
    if (p.image && p.images && p.images.length) {
      const idx = p.images.findIndex(i => i === p.image);
      if (idx >= 0) mainIndex = idx;
    }

    setForm({
      name: p.name || "",
      description: p.description || "",
      price: p.price ?? "",
      salePrice: p.salePrice ?? "",
      offerPrice: p.offerPrice ?? '',
      offerFrom: p.offerFrom ?? '',
      offerTo: p.offerTo ?? '',
      categoryId: p.categoryId || "",
      category: p.categoryId || "", // Keep for select value compatibility
      sku: p.sku || "",
      brand: p.brand || "",
      garancia: p.garancia || "",
      modeli: p.modeli || "",
      stock: p.stock ?? "",
      specs: "", // We rely on specValues now
      specValues: initialSpecValues,
      images: p.images && p.images.length ? p.images.slice() : [],
      serviceIds: Array.isArray(p.services) ? p.services.map(s => s.id) : [],
      mainImageIndex: mainIndex,
      isActive: p.isActive !== false,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await deleteProduct(id);
      setProducts(products.filter(p => p.id !== id));
      toast.success('Produkti u fshi me sukses');
    } catch (err) {
      console.error(err);
      toast.error('Gabim gjatë fshirjes së produktit');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    // Cleanup fields
    if (payload.salePrice === "") delete payload.salePrice;
    if (!payload.offerPrice) delete payload.offerPrice;
    if (!payload.offerFrom) delete payload.offerFrom;
    if (!payload.offerTo) delete payload.offerTo;

    if (payload.specValues && Object.keys(payload.specValues).length) {
      payload.specs = payload.specValues;
    }

    // Handle images
    if (payload.images && payload.images.length) {
      const files = payload.images.filter(i => i instanceof File);
      const strings = payload.images.filter(i => typeof i === 'string');
      // Fix main image
      if (typeof payload.mainImageIndex === 'number') {
        const sel = payload.images[payload.mainImageIndex];
        if (typeof sel === 'string') payload.image = sel;
      }
      if (files.length) {
        let filesOrdered = files.slice();
        // If main image is a file, move it to front
        if (typeof payload.mainImageIndex === 'number') {
          const sel = payload.images[payload.mainImageIndex];
          if (sel instanceof File) {
            const idx = filesOrdered.findIndex(f => f === sel);
            if (idx > 0) { const [m] = filesOrdered.splice(idx, 1); filesOrdered.unshift(m); }
            payload.mainUploadedIndex = filesOrdered.findIndex(f => f === sel);
          }
        }
        payload.images = strings.concat(filesOrdered);
        payload.imagesAction = 'replace';
      } else if (strings.length) {
        payload.images = strings;
        payload.imagesAction = 'replace';
      }
    }

    // Category ID to Name
    const findById = (nodes, id) => {
      for (const n of nodes) {
        if (!n) continue;
        if (String(n.id) === String(id)) return n;
        if (Array.isArray(n.subcategories)) { const r = findById(n.subcategories, id); if (r) return r; }
      }
      return null;
    };
    if (payload.categoryId || payload.category) {
      const targetId = payload.categoryId || payload.category;
      const catObj = findById(categories, targetId);
      if (catObj) {
        // We send both to be safe, but categoryId is the new primary
        payload.categoryId = catObj.id;
        payload.category = catObj.name;
      }
    }

    try {
      if (editId) {
        const resp = await updateProduct(editId, payload);
        const updated = (resp && resp.updated) ? resp.updated : resp;
        setProducts(products.map(p => p.id === editId ? updated : p));
      } else {
        const newP = await addProduct(payload);
        setProducts([newP, ...products]);
      }
      toast.success('Produkti u ruajt me sukses');
      setShowForm(false);
      setEditId(null);
    } catch (err) {
      toast.error(`Gabim gjatë ruajtjes së produktit: ${err.message}`);
    }
  };

  // Pagination for table
  const total = products.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const current = Math.min(page, totalPages);
  const pageItems = products.slice((current - 1) * perPage, current * perPage);

  // Helper for category display
  const getProductCategoryPath = (product) => {
    if (!product) return '—';
    if (Array.isArray(product.categories) && product.categories.length) {
      return product.categories.map(c => (typeof c === 'string' ? c : c.name)).join(' › ');
    }
    return product.category || '—';
  };

  return (
    <div>


      <div className="admin-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', flex: 1 }}>
          <div className="admin-form-group" style={{ marginBottom: 0, flex: 1, minWidth: 200 }}>
            <input className="admin-input" placeholder="Search by name..." value={searchQ} onChange={e => handleFilterChange(filterCategory, e.target.value)} />
          </div>
          <div className="admin-form-group" style={{ marginBottom: 0, width: 200 }}>
            <select className="admin-select" value={filterCategory} onChange={e => handleFilterChange(e.target.value, searchQ)}>
              <option value="">All Categories</option>
              {categories.map(cat => {
                const render = (node, prefix = '') => {
                  const opts = [<option key={node.id} value={node.id}>{prefix + node.name}</option>];
                  if (node.subcategories) node.subcategories.forEach(c => opts.push(...render(c, prefix + '-- ')));
                  return opts;
                };
                return render(cat);
              })}
            </select>
          </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ color: '#64748b', fontSize: 13, fontWeight: 500 }}>Manage catalog ({total} items)</div>
            <button className="admin-btn admin-btn-primary" onClick={handleStartAdd} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              Add Product
            </button>
          </div>
        </div>

        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th width="60">Img</th>
                <th>Name</th>
                <th>Category</th>
                <th>Brand</th>
                <th>Price</th>
                <th>Stock</th>
                <th width="140" style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.length === 0 ? <tr><td colSpan="7" className="admin-text" style={{ textAlign: 'center', padding: 24 }}>No products found.</td></tr> :
                pageItems.map(p => {
                  const info = priceInfo(p);
                  return (
                    <tr key={p.id} style={{ opacity: p.isActive === false ? 0.6 : 1 }}>
                      <td>
                        <div style={{ width: 48, height: 48, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {p.image || (p.images && p.images[0]) ? <img src={p.image || p.images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 9, color: '#94a3b8' }}>N/A</span>}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{p.name}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{p.sku} {p.isActive === false && <span style={{ color: '#ef4444', fontWeight: 600, marginLeft: 4 }}>(Inactive)</span>}</div>
                      </td>
                      <td style={{ fontSize: 13 }}>{getProductCategoryPath(p)}</td>
                      <td>{p.brand}</td>
                      <td>
                        <span style={{ fontWeight: 600 }}>{formatPrice(info.display)}</span>
                        {!!info.discounted && <div style={{ textDecoration: 'line-through', fontSize: 11, color: '#94a3b8' }}>{formatPrice(info.original)}</div>}
                      </td>
                      <td>{p.stock ?? '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => handleStartEdit(p)} style={{ marginRight: 6 }}>Edit</button>
                        <button className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => handleDelete(p.id)}>Delete</button>
                      </td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
          <div style={{ fontSize: 13, color: '#64748b' }}>Page {current} of {totalPages}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="admin-btn admin-btn-secondary admin-btn-sm" disabled={current <= 1} onClick={() => setPage(current - 1)}>&larr; Prev</button>
            <button className="admin-btn admin-btn-secondary admin-btn-sm" disabled={current >= totalPages} onClick={() => setPage(current + 1)}>Next &rarr;</button>
          </div>
        </div>
      </div>

      {/* Edit/Add Modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div className="admin-card" style={{ width: '100%', maxWidth: 900, maxHeight: '90vh', margin: 0, display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff' }}>
              <h3 className="admin-title" style={{ fontSize: 18, margin: 0 }}>{editId ? 'Edit Product' : 'Add Product'}</h3>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', fontSize: 24, padding: 0, color: '#64748b', cursor: 'pointer' }}>&times;</button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
              <form id="productForm" onSubmit={handleSubmit}>
                <div className="admin-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  {/* Left Col */}
                  <div>
                    <div className="admin-form-group">
                      <label className="admin-label">Product Name</label>
                      <input className="admin-input" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                    </div>
                    <div className="admin-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div className="admin-form-group">
                        <label className="admin-label">Price</label>
                        <input className="admin-input" type="number" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} />
                      </div>
                      <div className="admin-form-group">
                        <label className="admin-label">Sale Price</label>
                        <input className="admin-input" type="number" step="0.01" value={form.salePrice} onChange={e => setForm({ ...form, salePrice: e.target.value })} />
                      </div>
                    </div>
                    <div className="admin-form-group">
                      <label className="admin-label">Category</label>
                      <select className="admin-select" value={form.category} onChange={e => {
                        const val = e.target.value;
                        // Check for specs template
                        const findById = (nodes, id) => {
                          for (const n of nodes) {
                            if (String(n.id) === String(id)) return n;
                            if (n.subcategories) { const x = findById(n.subcategories, id); if (x) return x; }
                          }
                          return null;
                        };
                        findById(categories, val);
                        setForm({ ...form, category: val });
                      }}>
                        <option value="">Select Category</option>
                        {categories.map(cat => {
                          const render = (node, prefix = '') => {
                            const opts = [<option key={node.id} value={node.id}>{prefix + node.name}</option>];
                            if (node.subcategories) node.subcategories.forEach(n => opts.push(...render(n, prefix + '-- ')));
                            return opts;
                          };
                          return render(cat);
                        })}
                      </select>
                    </div>

                    {/* Dynamic Specs */}
                    {(() => {
                      const findById = (nodes, id) => {
                        for (const n of nodes) {
                          if (String(n.id) === String(id)) return n;
                          if (n.subcategories) { const x = findById(n.subcategories, id); if (x) return x; }
                        }
                        return null;
                      };
                      const c = form.category ? findById(categories, form.category) : null;
                      if (c && Array.isArray(c.specs) && c.specs.length > 0) {
                        return (
                          <div style={{ background: '#f8fafc', padding: 12, borderRadius: 8, marginBottom: 16, border: '1px solid #e2e8f0' }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8, textTransform: 'uppercase' }}>{c.name} Specs</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                              {c.specs.map(s => (
                                <div key={s}>
                                  <label className="admin-label" style={{ fontSize: 12 }}>{s}</label>
                                  <input className="admin-input" style={{ padding: '6px 10px' }} value={form.specValues[s] || ''} onChange={e => setForm({ ...form, specValues: { ...form.specValues, [s]: e.target.value } })} />
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}

                    <div className="admin-form-group">
                      <label className="admin-label">Description</label>
                      <textarea className="admin-textarea" rows={4} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                    </div>
                  </div>

                  {/* Right Col */}
                  <div>
                    <div className="admin-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div className="admin-form-group">
                        <label className="admin-label">Brand</label>
                        <input className="admin-input" value={form.brand} onChange={e => setForm({ ...form, brand: e.target.value })} />
                      </div>
                      <div className="admin-form-group">
                        <label className="admin-label">SKU</label>
                        <input className="admin-input" value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} />
                      </div>
                    </div>
                    <div className="admin-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div className="admin-form-group">
                        <label className="admin-label">Stock</label>
                        <input className="admin-input" type="number" value={form.stock} onChange={e => setForm({ ...form, stock: e.target.value })} />
                      </div>
                      <div className="admin-form-group">
                        <label className="admin-label">Warranty</label>
                        <input className="admin-input" value={form.garancia} onChange={e => setForm({ ...form, garancia: e.target.value })} />
                      </div>
                    </div>

                    <div className="admin-form-group">
                      <label className="admin-label">Active Status</label>
                      <div style={{ display: 'flex', gap: 12 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                          <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} />
                          Is Active Product
                        </label>
                      </div>
                    </div>

                    <div className="admin-form-group">
                      <label className="admin-label">Images</label>
                      <input type="file" className="admin-input" multiple accept="image/*" onChange={e => {
                        const files = e.target.files ? Array.from(e.target.files) : [];
                        setForm({ ...form, images: [...form.images, ...files] });
                      }} />
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                        {form.images.map((img, i) => {
                          let src = '';
                          if (img instanceof File) src = URL.createObjectURL(img);
                          else src = img;
                          const isMain = i === form.mainImageIndex;
                          return (
                            <div key={i} style={{ width: 60, height: 60, position: 'relative', border: isMain ? '2px solid #2563eb' : '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer' }} onClick={() => setForm({ ...form, mainImageIndex: i })}>
                              <img src={src} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '2px' }} alt="" />
                              <button type="button" onClick={(e) => {
                                e.stopPropagation();
                                const next = [...form.images];
                                next.splice(i, 1);
                                setForm({ ...form, images: next, mainImageIndex: 0 });
                              }} style={{ position: 'absolute', top: -5, right: -5, width: 18, height: 18, borderRadius: 9, background: 'red', color: 'white', border: 'none', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>&times;</button>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="admin-form-group">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <label className="admin-label" style={{ margin: 0 }}>Services</label>
                        <button type="button" className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => setShowServicesModal(true)} style={{ padding: '2px 8px', fontSize: 11 }}>Select Services</button>
                      </div>
                      <div style={{ fontSize: 13, color: '#64748b' }}>
                        {form.serviceIds.length} services selected
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            <div style={{ padding: 16, borderTop: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" form="productForm" className="admin-btn admin-btn-primary">Save Product</button>
            </div>
          </div>
        </div>
      )}

      {/* Services Selection Modal */}
      {showServicesModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 110, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="admin-card" style={{ width: 800, margin: 0, padding: 0, display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
            <div style={{ padding: 16, borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 className="admin-title" style={{ fontSize: 16, margin: 0 }}>Select & Order Services</h3>
              <button onClick={() => setShowServicesModal(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 20 }}>&times;</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, padding: 24, overflowY: 'auto' }}>
              {/* Selected Services (Sortable) */}
              <div>
                <div style={{ fontWeight: 600, marginBottom: 16, fontSize: 14 }}>Selected Services (Drag to Reorder)</div>
                {form.serviceIds.length === 0 ? (
                  <div style={{ padding: 24, background: '#f8fafc', borderRadius: 8, border: '1px dashed #cbd5e1', textAlign: 'center', color: '#64748b', fontSize: 13 }}>
                    No services selected yet.<br />Select from the available list.
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: 8 }}>
                    {form.serviceIds.map((id, index) => {
                      const svc = availableServices.find(s => s.id === id);
                      if (!svc) return null;
                      return (
                        <div
                          key={id}
                          draggable
                          onDragStart={(e) => {
                            setDraggedServiceId(id);
                            e.dataTransfer.effectAllowed = 'move';
                            // Slightly dim the item being dragged
                            setTimeout(() => {
                              if (e.target && e.target.style) e.target.style.opacity = '0.5';
                            }, 0);
                          }}
                          onDragEnd={(e) => {
                            setDraggedServiceId(null);
                            if (e.target && e.target.style) e.target.style.opacity = '1';
                          }}
                          onDragOver={(e) => {
                            e.preventDefault(); // Necessary to allow dropping
                            e.dataTransfer.dropEffect = 'move';
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            if (!draggedServiceId || draggedServiceId === id) return;

                            const draggedIndex = form.serviceIds.indexOf(draggedServiceId);
                            const dropIndex = index;

                            if (draggedIndex === -1 || dropIndex === -1) return;

                            const newServiceIds = [...form.serviceIds];
                            newServiceIds.splice(draggedIndex, 1);
                            newServiceIds.splice(dropIndex, 0, draggedServiceId);

                            setForm({ ...form, serviceIds: newServiceIds });
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            padding: '10px 12px',
                            background: draggedServiceId === id ? '#f0f9ff' : '#fff',
                            border: '1px solid #e2e8f0',
                            borderRadius: 6,
                            cursor: 'grab',
                            transition: 'background 0.2s',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                          }}
                        >
                          <div style={{ color: '#94a3b8', cursor: 'grab', display: 'flex' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="9" cy="5" r="1"></circle>
                              <circle cx="9" cy="12" r="1"></circle>
                              <circle cx="9" cy="19" r="1"></circle>
                              <circle cx="15" cy="5" r="1"></circle>
                              <circle cx="15" cy="12" r="1"></circle>
                              <circle cx="15" cy="19" r="1"></circle>
                            </svg>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{svc.name}</div>
                            <div style={{ fontSize: 12, color: '#64748b' }}>{formatPrice(svc.price)}</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setForm({ ...form, serviceIds: form.serviceIds.filter(sid => sid !== id) })}
                            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 4 }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Available Services (Selection) */}
              <div style={{ borderLeft: '1px solid #e2e8f0', paddingLeft: 24 }}>
                <div style={{ fontWeight: 600, marginBottom: 16, fontSize: 14 }}>Available Services</div>
                <div style={{ display: 'grid', gap: 8 }}>
                  {availableServices.length === 0 && <div className="admin-text" style={{ fontSize: 13 }}>No services available.</div>}
                  {availableServices.filter(s => !form.serviceIds.includes(s.id)).map(s => (
                    <div
                      key={s.id}
                      onClick={() => setForm({ ...form, serviceIds: [...form.serviceIds, s.id] })}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px 12px',
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: 6,
                        cursor: 'pointer',
                        transition: 'background 0.2s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.background = '#f1f5f9'}
                      onMouseOut={(e) => e.currentTarget.style.background = '#f8fafc'}
                    >
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{s.name}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>{formatPrice(s.price)}</div>
                      </div>
                      <div style={{
                        width: 24, height: 24, borderRadius: '50%', background: '#fff', border: '1px solid #cbd5e1',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3b82f6', fontSize: 16, fontWeight: 300
                      }}>+</div>
                    </div>
                  ))}
                  {availableServices.filter(s => !form.serviceIds.includes(s.id)).length === 0 && availableServices.length > 0 && (
                    <div className="admin-text" style={{ fontSize: 13, textAlign: 'center', padding: 16 }}>All services are selected.</div>
                  )}
                </div>
              </div>
            </div>

            <div style={{ padding: '16px 24px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', background: '#f8fafc', borderRadius: '0 0 8px 8px' }}>
              <button className="admin-btn admin-btn-primary" onClick={() => setShowServicesModal(false)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
